import React, { useState, useEffect, useRef } from 'react';
import { Car, Heart, Loader2, AlertCircle, ChevronDown, Star, TrendingUp, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes.constants';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import AgencyCTA from '../components/vehicule/agencyCTA';
import HeroSearch from '../components/vehicule/HeroSearch';
import BrandFilter from '../components/vehicule/BrandFilter';
import VehicleCard from '../components/vehicule/VehicleCard';
import Button from '../components/common/Button';
import Logo from '../components/common/Logo';
import vehicleService from '../services/vehicleService';
import { formatVehicleForCard } from '../utils/vehicleUtils';

const HomePage = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesRaw, setVehiclesRaw] = useState([]); // Garder les données brutes de l'API
  const [allFormattedVehicles, setAllFormattedVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [sortBy, setSortBy] = useState('rating_desc');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef(null);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Options de tri avec icônes
  const sortOptions = [
    { value: 'rating_desc', label: t('vehicles.bestRated'), icon: Star },
    { value: 'price_asc', label: t('vehicles.priceAsc'), icon: TrendingUp },
    { value: 'nearest', label: t('vehicles.nearestLocation'), icon: MapPin },
  ];

  // Helper function pour formater les véhicules depuis l'API au format attendu par VehicleCard
  const formatVehicles = (vehiclesData) => {
    // La structure de la réponse API est: { success: true, data: { vehicles: [...] } }
    // vehiclesData est déjà vehiclesResponse.data, donc on accède à vehiclesData.vehicles
    // Gérer aussi le cas où vehiclesData pourrait être directement le tableau (fallback)
    const vehiclesArray = vehiclesData?.vehicles || (Array.isArray(vehiclesData) ? vehiclesData : []);
    
    // S'assurer que c'est bien un tableau
    if (!Array.isArray(vehiclesArray)) {
      console.error('formatVehicles: vehiclesArray is not an array', vehiclesArray);
      return [];
    }
    
    return vehiclesArray
      .map((vehicle) => formatVehicleForCard(vehicle))
      .filter((vehicle) => vehicle !== null);
  };

  // --- Effects (Data Fetching) ---
  useEffect(() => {
    const initData = async () => {
      try {
        setIsLoading(true);
        
        // Charger les véhicules depuis l'API
        const vehiclesResponse = await vehicleService.getAllVehicles({
          isActive: true,
          isVerified: true,
          limit: 100,
        });
        
        if (vehiclesResponse.success) {
          // Garder les données brutes de l'API (tableau de véhicules)
          const rawVehicles = vehiclesResponse.data?.vehicles || vehiclesResponse.data || [];
          setVehiclesRaw(Array.isArray(rawVehicles) ? rawVehicles : []);
          
          // Transformer les données de l'API au format attendu
          const formattedVehicles = formatVehicles(vehiclesResponse.data);
          
          setVehicles(formattedVehicles);
          setAllFormattedVehicles(formattedVehicles);
          
          // Extraire les marques uniques depuis les véhicules
          const uniqueBrands = [...new Set(formattedVehicles.map(v => v.make))].map((brand, index) => ({
            id: brand.toLowerCase(),
            label: brand,
          }));
          setBrands([{ id: 'all', label: t('vehicles.allBrands') }, ...uniqueBrands]);
        } else {
          setError(vehiclesResponse.message || "Erreur lors du chargement des véhicules.");
        }
      } catch (err) {
        console.error('Error loading vehicles:', err);
        setError("Erreur lors du chargement des données.");
      } finally {
        setIsLoading(false);
      }
    };
    initData();
  }, [t]);

  // --- Handlers ---
  const handleBrandChange = (brandIds) => {
    setSelectedBrands(Array.isArray(brandIds) ? brandIds : []);
  };

  const handleSortChange = async (newSort) => {
    setSortBy(newSort);
    const sorted = sortVehicles([...vehicles], newSort);
    setVehicles(sorted);
  };

  // Fonction helper pour trier les véhicules
  const sortVehicles = (vehiclesList, sortOption) => {
    const sorted = [...vehiclesList];
    if (sortOption === 'price_asc') {
      sorted.sort((a, b) => a.price - b.price);
    } else if (sortOption === 'rating_desc') {
      sorted.sort((a, b) => b.rating - a.rating);
    }
    // Pour 'nearest', on garde l'ordre actuel (sera implémenté avec géolocalisation plus tard)
    return sorted;
  };

  useEffect(() => {
    const filteredVehicles = allFormattedVehicles.filter(v => 
      selectedBrands.length === 0 || 
      selectedBrands.some(brandId => v.make.toLowerCase() === brandId.toLowerCase())
    );
    setVehicles(sortVehicles(filteredVehicles, sortBy));
  }, [selectedBrands, allFormattedVehicles, sortBy]);

  return (
    <>
      <HeroSearch />
      
      <BrandFilter 
        brands={brands} 
        selectedBrands={selectedBrands} 
        onSelect={handleBrandChange} 
      />

      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {selectedBrands.length === 0
                ? (t('vehicles.allOffers') || "Toutes les offres")
                : selectedBrands.length === 1
                  ? `${t('vehicles.offers') || "Offres"} ${brands.find(b => b.id === selectedBrands[0])?.label || selectedBrands[0]}`
                  : "Offres sélectionnées"
              }
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isLoading ? t('vehicles.loading') : `${vehicles.length} ${t('vehicles.available')}`}
            </p>
          </div>
          
          {/* Sort Dropdown */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden sm:block">{t('vehicles.sortBy')}</span>
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setIsSortOpen(!isSortOpen)}
                className="flex items-center gap-2 bg-white border-2 border-gray-200 text-gray-700 text-sm font-medium rounded-xl px-4 py-2.5 cursor-pointer hover:border-[#27489E] transition shadow-sm min-w-[160px] justify-between"
              >
                <div className="flex items-center gap-2">
                  {(() => {
                    const option = sortOptions.find(o => o.value === sortBy);
                    const Icon = option?.icon;
                    return Icon ? <Icon className="w-4 h-4 text-[#27489E]" /> : null;
                  })()}
                  <span>{sortOptions.find(o => o.value === sortBy)?.label}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
              </button>

              {isSortOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[200px] z-50 animate-in fade-in slide-in-from-top-2">
                  {sortOptions.map((option) => {
                    const Icon = option.icon;
                    const isActive = sortBy === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          handleSortChange(option.value);
                          setIsSortOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${
                          isActive 
                            ? 'bg-[#27489E]/10 text-[#27489E]' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isActive ? 'bg-[#27489E] text-white' : 'bg-gray-100 text-gray-500'
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="font-medium">{option.label}</span>
                        {isActive && (
                          <div className="ml-auto w-2 h-2 rounded-full bg-[#27489E]"></div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-2 mb-8">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-[#27489E] animate-spin mb-4" />
            <p className="text-gray-400 font-medium">{t('vehicles.searchingBestOffers')}</p>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200">
            <Car className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-600">{t('vehicles.noVehicleFound')}</h3>
            <p className="text-gray-400 mb-6">{t('vehicles.changeFilters')}</p>
            <Button variant="outline" onClick={() => handleBrandChange([])}>
              {t('vehicles.viewAllCatalog')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {vehicles.map((vehicle) => {
              // Trouver le véhicule brut correspondant pour passer les données complètes au modal
              const rawVehicle = vehiclesRaw.find(v => v._id === vehicle.id || v._id === vehicle._id);
              // Fusionner les données formatées avec les données brutes pour avoir toutes les infos
              // S'assurer que rating est toujours un nombre valide et que location reste une string
              const vehicleWithFullData = rawVehicle 
                ? { 
                    ...rawVehicle, // Commencer par les données brutes
                    ...vehicle,    // Puis écraser avec les données formatées (location string, rating, etc.)
                    // S'assurer que rating reste un nombre (priorité au rating formaté si le brut est invalide)
                    rating: typeof vehicle.rating === 'number' && !isNaN(vehicle.rating) 
                      ? vehicle.rating 
                      : (typeof rawVehicle.rating === 'number' && !isNaN(rawVehicle.rating) 
                          ? rawVehicle.rating 
                          : 4.5),
                    // S'assurer que location reste une string (format attendu par VehicleCard)
                    location: typeof vehicle.location === 'string' 
                      ? vehicle.location 
                      : (rawVehicle.location?.city || rawVehicle.vehicleLocation?.cityName || 'N/A') + ', Maroc'
                  } 
                : vehicle;
              
              return (
                <VehicleCard 
                  key={vehicle.id} 
                  vehicle={vehicleWithFullData}
                  isLoggedIn={isAuthenticated}
                  onLoginClick={() => navigate(ROUTES.LOGIN)}
                />
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <Button variant="outline" className="mx-auto" onClick={() => navigate(ROUTES.VEHICLES)}>
            {t('vehicles.viewAllVehicles')}
          </Button>
        </div>
      </main>

      {/* Agency CTA + App Download */}
      <AgencyCTA />

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4 opacity-60">
            <Logo 
              size="text-2xl" 
              carColor="text-[#27489E]" 
              vyColor="text-[#F5A623]"
            />
          </div>
          <p className="text-sm text-gray-400 mb-8">
            &copy; 2025 Carvy Tech SARL. {t('footer.madeWith')} <Heart className="w-3 h-3 inline text-red-400" /> {t('footer.inMorocco')}.
          </p>
        </div>
      </footer>
    </>
  );
};

export default HomePage;

